// ============================================================
// PublicIntake.jsx — 일반인 매물접수 폼 (/register)
//
// [목적]
//  블로그·QR·문자로 들어온 집주인이 로그인 없이 매물을 접수한다.
//  접수 내용은 estate_submissions 에 쌓이고, 관리자가 승인해야
//  실제 매물장(estate)에 올라간다.
//
// [흐름]
//  1단계 본인확인 : 휴대폰 → 인증번호(SMS) → 확인
//  2단계 매물정보 : 카카오 주소검색 + 종류별 항목
//  3단계 동의     : 개인정보 수집·이용 (법적 필수)
//  → POST /api/public-intake/submit
//  → 승인 시 서버가 건물정보(준공일·층수 등)를 자동 조회한다
//
// [연관]
//  backend/routes/publicIntake.js  send-code / verify / submit
//  backend/routes/staffIntake.js   승인 처리
//  pages/PublicIntake.css
//  public/index.html 의 daum postcode 스크립트 (주소검색)
// ============================================================
import React, { useState, useEffect } from "react";
import "./PublicIntake.css";

// 매물 종류 — estate.bld_type 코드값과 동일해야 승인 시 그대로 옮겨진다
const KINDS = [
  { value: "single-room",     label: "원룸" },
  { value: "two-room",        label: "투룸" },
  { value: "multi-household", label: "다가구" },
  { value: "villa",           label: "빌라" },
  { value: "apt",             label: "아파트" },
  { value: "e-apt",           label: "오피스텔" },
  { value: "shop",            label: "상가" },
  { value: "office",          label: "사무실" },
];

// 룸수·욕실수를 물어볼 종류 (상가·사무실은 제외)
const ROOM_KINDS = ["single-room","two-room","multi-household","villa","apt","e-apt"];
// 상호명·권리금을 물어볼 종류
const SHOP_KINDS = ["shop", "office"];

const DEALS = [
  { value: "month",  label: "월세" },
  { value: "jeonse", label: "전세" },
  { value: "sale",   label: "매매" },
];

// 관리비 방식
const MGR_TYPES = [
  { value: "none",  label: "없음" },
  { value: "rule",  label: "관리규정에 따름" },
  { value: "fixed", label: "정액" },
];

// 관리비 포함 항목 — estate 의 price_mgr_item_* 컬럼과 짝을 맞춘다
const MGR_ITEMS = [
  { key: "basic",    label: "기본" },
  { key: "water",    label: "수도" },
  { key: "elec",     label: "전기" },
  { key: "heat",     label: "난방" },
  { key: "internet", label: "인터넷" },
  { key: "online",   label: "TV" },
];

// 입주 가능 시기
const MOVE_TYPES = [
  { value: "empty",  label: "공실" },
  { value: "any",    label: "언제든지" },
  { value: "nego",   label: "협의" },
  { value: "date",   label: "날짜 지정" },
  { value: "etc",    label: "기타" },
];

const fmtPhone = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

// 호수에서 층을 추론한다 (직원 폼과 같은 규칙)
//   4자리(1203)→12층, 3자리(302)→3층, 2자리 이하→1층, B/지하 포함→지하
// [INTAKE_FIX_V3] 자유 입력 대응 — '호' 앞 숫자 우선, 동 번호 무시, B·지·지하 바로 뒤 숫자만 지하
//   "301호" → 3   (예전엔 '호' 까지 4글자로 세어 30층)
//   "101동 301호" → 3 · "1203" → 12 · "B101"·"지1"·"지하 1" → -1 · "B동 301호" → 3
//   직원폼(StaffIntake.jsx) 키패드 입력은 같은 결과를 내도록 따로 고쳐져 있음 (INTAKE_FIX_V1·V2)
const inferFloor = (ho) => {
  const s = String(ho || "").trim().toUpperCase();
  const groups = [...s.matchAll(/[0-9]+/g)];          // 숫자 덩어리 전부 (동·호 구분용)
  if (!groups.length) return "";
  // '호' 바로 앞 숫자를 우선, 없으면 마지막 숫자
  const withHo = groups.find((m) => /^\s*호/.test(s.slice(m.index + m[0].length)));
  const m = withHo || groups[groups.length - 1];
  const digits = m[0];
  // 지하: 그 숫자 바로 앞이 B / 지 / 지하 (B동 같은 동 이름은 지하 아님)
  const before = s.slice(0, m.index).replace(/\s+$/, "");
  const isBasement = /(B|지하?)-?$/.test(before);

  if (isBasement) {
    return digits.length >= 3
      ? `-${digits.substring(0, digits.length - 2)}`
      : "-1";
  }
  if (digits.length === 4) return digits.substring(0, 2);
  if (digits.length === 3) return digits.substring(0, 1);
  return "1";
};

export default function PublicIntake() {
  // ── 1단계: 본인확인 ──
  const [phone,    setPhone]    = useState("");
  const [code,     setCode]     = useState("");
  const [sent,     setSent]     = useState(false);
  const [verified, setVerified] = useState(false);
  const [token,    setToken]    = useState(null);
  const [left,     setLeft]     = useState(0);

  // ── 2단계: 접수자 ──
  const [name,     setName]     = useState("");
  const [relation, setRelation] = useState("소유자");

  // ── 2단계: 주소 ──
  const [jibun,  setJibun]  = useState("");   // 카카오가 준 지번주소
  const [road,   setRoad]   = useState("");   // 카카오가 준 도로명주소
  const [ho,     setHo]     = useState("");   // 상세주소(호수)
  const [floor,  setFloor]  = useState("");   // 호수에서 자동 추론

  // ── 2단계: 매물 ──
  const [kind,    setKind]    = useState("single-room");
  const [rooms,   setRooms]   = useState("1");
  const [baths,   setBaths]   = useState("1");
  const [shopName, setShopName] = useState("");
  const [premium,  setPremium]  = useState("");

  // ── 2단계: 거래 ──
  const [deal,    setDeal]    = useState("month");
  const [deposit, setDeposit] = useState("");
  const [monthly, setMonthly] = useState("");
  const [jeonse,  setJeonse]  = useState("");
  const [saleP,   setSaleP]   = useState("");

  // ── 2단계: 관리비 ──
  const [mgrType, setMgrType] = useState("none");
  const [mgrFee,  setMgrFee]  = useState("");
  const [mgrItems, setMgrItems] = useState({});

  // ── 2단계: 입주 ──
  const [moveType, setMoveType] = useState("nego");
  const [moveDate, setMoveDate] = useState("");
  const [moveEtc,  setMoveEtc]  = useState("");

  // ── 2단계: 사진·메모 ──
  const [photos, setPhotos] = useState([]);   // File 배열, 최대 3장
  const [memo,   setMemo]   = useState("");

  // ── 3단계 ──
  const [agree, setAgree] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // 봇 함정
  const [website, setWebsite] = useState("");

  const [busy, setBusy] = useState(false);
  const [msg,  setMsg]  = useState("");
  const [done, setDone] = useState(false);

  // 로그인한 직원·관리자는 본인확인을 건너뛴다.
  //   화면 편의를 위한 것이고, 실제 검증은 서버가 토큰으로 한다.
  //   (여기를 우회해도 서버가 막으므로 안전하다)
  const isStaff = !!localStorage.getItem("token");
  useEffect(() => {
    if (isStaff) setVerified(true);
  }, [isStaff]);

  useEffect(() => {
    if (left <= 0) return;
    const t = setInterval(() => setLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [left]);

  // 호수를 입력하면 층을 자동으로 채운다 (직접 고치면 그 값을 유지)
  useEffect(() => {
    if (!ho) return;
    const f = inferFloor(ho);
    if (f) setFloor(f);
  }, [ho]);

  const api = async (path, body) => {
    // 로그인 상태면 토큰을 함께 보낸다.
    // 서버가 직원·관리자로 확인하면 SMS 인증을 건너뛴다.
    const tk = localStorage.getItem("token");
    const r = await fetch(`/api/public-intake/${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(tk ? { Authorization: `Bearer ${tk}` } : {}),
      },
      body: JSON.stringify(body),
    });
    return r.json();
  };

  // ── 카카오 주소검색 ──
  const openPostcode = () => {
    if (!window.daum?.Postcode) {
      return setMsg("주소 검색을 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.");
    }
    new window.daum.Postcode({
      oncomplete: (d) => {
        setJibun(d.jibunAddress || d.autoJibunAddress || "");
        setRoad(d.roadAddress || "");
        setMsg("");
      },
    }).open();
  };

  const sendCode = async () => {
    const digits = phone.replace(/\D/g, "");
    if (!/^01[0-9]{8,9}$/.test(digits)) {
      return setMsg("휴대폰 번호를 정확히 입력해 주세요.");
    }
    setBusy(true); setMsg("");
    try {
      const j = await api("send-code", { phone: digits });
      if (!j.ok) {
        const r = {
          phone_limit: "이 번호로는 오늘 더 보낼 수 없습니다. 내일 다시 시도해 주세요.",
          ip_limit: "요청이 많습니다. 잠시 후 다시 시도해 주세요.",
          invalid_phone: "휴대폰 번호를 정확히 입력해 주세요.",
        };
        return setMsg(r[j.error] || "발송에 실패했습니다.");
      }
      setSent(true);
      setLeft((j.ttl_min || 3) * 60);
      setMsg("인증번호를 보냈습니다. 문자를 확인해 주세요.");
    } catch (e) {
      setMsg("발송에 실패했습니다: " + e.message);
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setMsg("");
    try {
      const j = await api("verify", { phone: phone.replace(/\D/g, ""), code });
      if (!j.ok) {
        const r = {
          expired: "인증 시간이 지났습니다. 다시 받아주세요.",
          too_many_tries: "너무 여러 번 틀렸습니다. 다시 받아주세요.",
          wrong_code: `번호가 맞지 않습니다.${j.left != null ? ` (${j.left}회 남음)` : ""}`,
          not_found: "먼저 인증번호를 받아주세요.",
        };
        return setMsg(r[j.error] || "확인에 실패했습니다.");
      }
      setVerified(true); setToken(j.token); setMsg("");
    } catch (e) {
      setMsg("확인에 실패했습니다: " + e.message);
    } finally { setBusy(false); }
  };

  const pickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos((p) => [...p, ...files].slice(0, 3));   // 최대 3장
    e.target.value = "";
  };

  const submit = async () => {
    if (!jibun)  return setMsg("주소를 검색해 주세요.");
    if (isStaff && !phone.replace(/\D/g, "")) {
      return setMsg("연락처를 입력해 주세요.");
    }
    if (!agree)  return setMsg("개인정보 수집·이용에 동의해 주세요.");

    setBusy(true); setMsg("");
    try {
      // 사진을 base64 로 바꿔 함께 보낸다 (3장 이하라 부담이 적다)
      const photoData = [];
      for (const f of photos) {
        const b64 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(String(r.result).split(",")[1]);
          r.onerror = () => res(null);
          r.readAsDataURL(f);
        });
        if (b64) photoData.push({ name: f.name, type: f.type, data: b64 });
      }

      const j = await api("submit", {
        token,
        phone: phone.replace(/\D/g, ""),
        name, relation, agree, website,
        payload: {
          jibunAddress: jibun,
          roadAddress: road,
          ho, floor,
          bld_type: kind,
          estate_type: SHOP_KINDS.includes(kind) ? "commerce" : "residence",
          room_cnt: ROOM_KINDS.includes(kind) ? rooms : "",
          bath_cnt: ROOM_KINDS.includes(kind) ? baths : "",
          shop_name: SHOP_KINDS.includes(kind) ? shopName : "",
          price_premium: SHOP_KINDS.includes(kind) ? premium : "",
          deal, deposit, monthly, jeonse, price_sale: saleP,
          mgr_type: mgrType,
          mgr_fee: mgrType === "fixed" ? mgrFee : "",
          mgr_items: mgrType === "fixed" ? mgrItems : {},
          move_type: moveType,
          move_date: moveType === "date" ? moveDate : "",
          move_etc:  moveType === "etc"  ? moveEtc  : "",
          memo,
        },
        photos: photoData,
      });
      if (!j.ok) return setMsg("접수에 실패했습니다. 잠시 후 다시 시도해 주세요.");
      setDone(true);
    } catch (e) {
      setMsg("접수에 실패했습니다: " + e.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="pi-wrap">
        <div className="pi-done">
          <div className="pi-done-icon">✓</div>
          <h2>접수가 완료되었습니다</h2>
          <p>담당자가 확인 후 <b>{phone}</b> 으로<br />연락드리겠습니다.</p>
          <p className="pi-done-sub">모모부동산 02-878-5989</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pi-wrap">
      <div className="pi-head">
        <div className="pi-logo">MOMO</div>
        <h1>{isStaff ? "매물 대리 접수" : "내 물건 내놓기"}</h1>
        <p>
          {isStaff
            ? "관리자 모드 · 본인확인 없이 바로 접수됩니다"
            : "관악구 원룸·투룸 전문 · 접수 후 담당자가 연락드립니다"}
        </p>
      </div>

      {msg && <div className="pi-msg">{msg}</div>}

      {/* ── 1. 본인확인 (로그인 상태면 생략) ── */}
      {!isStaff && (
      <div className="pi-sec">
        <div className="pi-step">1. 본인확인</div>
        <div className="pi-row">
          <input className="pi-input" inputMode="tel" value={phone} disabled={verified}
            onChange={(e) => setPhone(fmtPhone(e.target.value))} placeholder="휴대폰 번호" />
          {!verified && (
            <button className="pi-btn-sm" disabled={busy} onClick={sendCode}>
              {sent ? "재발송" : "인증번호"}
            </button>
          )}
          {verified && <span className="pi-ok">확인됨</span>}
        </div>
        {sent && !verified && (
          <div className="pi-row">
            <input className="pi-input" inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="인증번호 6자리" />
            {left > 0 && (
              <span className="pi-timer">
                {String(Math.floor(left / 60)).padStart(2, "0")}:
                {String(left % 60).padStart(2, "0")}
              </span>
            )}
            <button className="pi-btn-sm" disabled={busy} onClick={verify}>확인</button>
          </div>
        )}
      </div>
      )}

      {verified && (
        <>
          {/* ── 2. 접수자 ── */}
          <div className="pi-sec">
            <div className="pi-step">2. 접수자</div>
            <div className="pi-row">
              <input className="pi-input" value={name}
                onChange={(e) => setName(e.target.value)} placeholder="성함" />
              <select className="pi-input pi-rel" value={relation}
                onChange={(e) => setRelation(e.target.value)}>
                <option>소유자</option>
                <option>임차인</option>
              </select>
            </div>
            {/* 관리자가 대신 입력할 때는 인증 단계가 없으므로
                연락처를 여기서 직접 받는다. */}
            {isStaff && (
              <div className="pi-row">
                <input className="pi-input" inputMode="tel" value={phone}
                  onChange={(e) => setPhone(fmtPhone(e.target.value))}
                  placeholder="연락처 (예: 010-1234-5678)" />
              </div>
            )}
          </div>

          {/* ── 3. 주소 ── */}
          <div className="pi-sec">
            <div className="pi-step">3. 매물 주소</div>
            <button className="pi-addr-btn" onClick={openPostcode}>
              {jibun ? "주소 다시 검색" : "주소 검색"}
            </button>
            {jibun && (
              <div className="pi-addr-box">
                {road && <div><b>도로명</b> {road}</div>}
                <div><b>지번</b> {jibun}</div>
              </div>
            )}
            <div className="pi-row" style={{ marginTop: 8 }}>
              <input className="pi-input" value={ho}
                onChange={(e) => setHo(e.target.value)} placeholder="동·호수 (예: 301)" />
              <input className="pi-input pi-floor" inputMode="numeric" value={floor}
                onChange={(e) => setFloor(e.target.value)} placeholder="층" />
            </div>
            <div className="pi-hint">호수를 넣으면 층이 자동으로 채워집니다</div>
          </div>

          {/* ── 4. 매물 종류 ── */}
          <div className="pi-sec">
            <div className="pi-step">4. 매물 정보</div>
            <label className="pi-label">종류</label>
            <div className="pi-chips">
              {KINDS.map((k) => (
                <button key={k.value}
                  className={"pi-chip" + (kind === k.value ? " on" : "")}
                  onClick={() => setKind(k.value)}>{k.label}</button>
              ))}
            </div>

            {ROOM_KINDS.includes(kind) && (
              <>
                <label className="pi-label">방 개수</label>
                <div className="pi-nums">
                  {["1","2","3","4","5"].map((n) => (
                    <button key={n} className={"pi-chip" + (rooms === n ? " on" : "")}
                      onClick={() => setRooms(n)}>{n}</button>
                  ))}
                </div>
                <label className="pi-label">욕실 개수</label>
                <div className="pi-nums">
                  {["1","2","3"].map((n) => (
                    <button key={n} className={"pi-chip" + (baths === n ? " on" : "")}
                      onClick={() => setBaths(n)}>{n}</button>
                  ))}
                </div>
              </>
            )}

            {SHOP_KINDS.includes(kind) && (
              <>
                <label className="pi-label">상호명</label>
                <input className="pi-input full" value={shopName}
                  onChange={(e) => setShopName(e.target.value)} placeholder="예: 모모커피" />
                <label className="pi-label">권리금 (만원)</label>
                <input className="pi-input full" inputMode="numeric" value={premium}
                  onChange={(e) => setPremium(e.target.value)} placeholder="없으면 비워두세요" />
              </>
            )}
          </div>

          {/* ── 5. 거래 조건 ── */}
          <div className="pi-sec">
            <div className="pi-step">5. 거래 조건</div>
            <div className="pi-chips">
              {DEALS.map((d) => (
                <button key={d.value}
                  className={"pi-chip" + (deal === d.value ? " on" : "")}
                  onClick={() => setDeal(d.value)}>{d.label}</button>
              ))}
            </div>

            <label className="pi-label">희망 금액 (만원)</label>
            {deal === "month" && (
              <div className="pi-row">
                <input className="pi-input" inputMode="numeric" value={deposit}
                  onChange={(e) => setDeposit(e.target.value)} placeholder="보증금" />
                <input className="pi-input" inputMode="numeric" value={monthly}
                  onChange={(e) => setMonthly(e.target.value)} placeholder="월세" />
              </div>
            )}
            {deal === "jeonse" && (
              <input className="pi-input full" inputMode="numeric" value={jeonse}
                onChange={(e) => setJeonse(e.target.value)} placeholder="전세보증금" />
            )}
            {deal === "sale" && (
              <input className="pi-input full" inputMode="numeric" value={saleP}
                onChange={(e) => setSaleP(e.target.value)} placeholder="매매가" />
            )}

            <label className="pi-label">관리비</label>
            <div className="pi-chips">
              {MGR_TYPES.map((m) => (
                <button key={m.value}
                  className={"pi-chip" + (mgrType === m.value ? " on" : "")}
                  onClick={() => setMgrType(m.value)}>{m.label}</button>
              ))}
            </div>
            {mgrType === "fixed" && (
              <>
                <div className="pi-row" style={{ marginTop: 8 }}>
                  <input className="pi-input" inputMode="numeric" value={mgrFee}
                    onChange={(e) => setMgrFee(e.target.value)} placeholder="금액" />
                  <span className="pi-unit">만원</span>
                </div>
                <label className="pi-label">관리비 포함 항목</label>
                <div className="pi-chips">
                  {MGR_ITEMS.map((m) => (
                    <button key={m.key}
                      className={"pi-chip" + (mgrItems[m.key] ? " on" : "")}
                      onClick={() => setMgrItems((p) => ({ ...p, [m.key]: !p[m.key] }))}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <label className="pi-label">입주 가능</label>
            <div className="pi-chips">
              {MOVE_TYPES.map((m) => (
                <button key={m.value}
                  className={"pi-chip" + (moveType === m.value ? " on" : "")}
                  onClick={() => setMoveType(m.value)}>{m.label}</button>
              ))}
            </div>
            {moveType === "date" && (
              <input className="pi-input full" type="date" value={moveDate}
                style={{ marginTop: 8 }}
                onChange={(e) => setMoveDate(e.target.value)} />
            )}
            {moveType === "etc" && (
              <input className="pi-input full" value={moveEtc} style={{ marginTop: 8 }}
                onChange={(e) => setMoveEtc(e.target.value)} placeholder="직접 입력" />
            )}
          </div>

          {/* ── 6. 사진 ── */}
          <div className="pi-sec">
            <div className="pi-step">6. 사진 (선택 · 최대 3장)</div>
            <div className="pi-photos">
              {photos.map((f, i) => (
                <div className="pi-photo" key={i}>
                  <img src={URL.createObjectURL(f)} alt="" />
                  <button className="pi-photo-del"
                    onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
              {photos.length < 3 && (
                <label className="pi-photo-add">
                  +
                  <input type="file" accept="image/*" multiple
                    style={{ display: "none" }} onChange={pickPhotos} />
                </label>
              )}
            </div>
          </div>

          {/* ── 7. 메모 ── */}
          <div className="pi-sec">
            <div className="pi-step">7. 남기실 말씀</div>
            <textarea className="pi-textarea" value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="특이사항이 있으면 적어주세요 (선택)" />
            <input className="pi-hp" tabIndex={-1} autoComplete="off"
              value={website} onChange={(e) => setWebsite(e.target.value)} />
          </div>

          {/* ── 8. 동의 ── */}
          <div className="pi-sec">
            <label className="pi-agree">
              <input type="checkbox" checked={agree}
                onChange={(e) => setAgree(e.target.checked)} />
              <span>개인정보 수집·이용에 동의합니다 <span className="pi-req">*</span></span>
            </label>
            <button className="pi-link" onClick={() => setShowTerms(!showTerms)}>
              {showTerms ? "닫기" : "자세히 보기"}
            </button>
            {showTerms && (
              <div className="pi-terms">
                <b>개인정보 수집·이용 안내</b>
                <p>· 수집 항목: 성명, 휴대폰번호, 매물 소재지 및 희망 조건, 사진</p>
                <p>· 수집 목적: 매물 접수 확인 및 중개 상담 연락</p>
                <p>· 보유 기간: 접수일로부터 1년 (요청 시 즉시 파기)</p>
                <p>· 동의를 거부하실 수 있으나, 이 경우 접수가 불가합니다.</p>
                <p className="pi-terms-sub">모모부동산 · 02-878-5989</p>
              </div>
            )}
          </div>

          <button className="pi-submit" disabled={busy || !agree} onClick={submit}>
            {busy ? "접수 중..." : "접수하기"}
          </button>
        </>
      )}
    </div>
  );
}